import React, { useEffect, useState, useRef, useContext } from 'react';
import { modalContext } from '@/context/modal.context';
import { viewportContext } from '@/context/viewport.context';
import { toastContext } from '@/context/toast.context';
import { AccountContext } from '@/context/account.context';
import classes from './Documents.module.scss';
import Button from '@/components/Button/Button';
import t from '@/utils/Language/Documents-translations';
import { Row, Col, Container, Card } from 'react-bootstrap';
import Dropzone from 'react-dropzone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import uploadedDocuments from '@/utils/api/odas/uploaded_documents';
import { languageContext } from '@/context/language.context';
import UserVerification from '../UserVerification/UserVerification';
import { isEmpty } from 'underscore';
import DocumentList from './List';
import useMixpanel from '@/hooks/useMixpanel';
import AgencyConfiguration from '@/utils/api/odas/agency_configuration';
import DocumentConsent from './Consent';

const Documents = ({ vaultId, agencyClient, ...props }) => {
  const { currentLanguage } = useContext(languageContext);
  const { enqueueModal, closeModal } = useContext(modalContext);
  const { unusedIsXSPhone, unusedIsPhone } = useContext(viewportContext);
  const { addToast } = useContext(toastContext);
  const [accountConfirmed, setAccountConfirmed] = useState();
  const [selectedFile, setSelectedFile] = useState();
  const [preview, setPreview] = useState();
  const [documents, setDocuments] = useState([]);
  const dropzoneRef = useRef();
  const account = useContext(AccountContext);
  const { track } = useMixpanel();
  const agencyClientId = agencyClient ? agencyClient.id : false;
  const [isPlusEnabled, setIsPlusEnabled] = useState(false);
  const ACCEPTED_FILE_TYPES = {
    'image/*': ['.png', '.jpeg', '.jpg'],
    'application/pdf': ['.pdf']
  };

  const [isLoading, setIsLoading] = useState({ account: true, documents: true });
  const [showConsent, setShowConsent] = useState(false);
  const [consentAgreed, setConsentAgreed] = useState(false);
  // Keeping below formats in comment for the future reference.
  // "text/plain, .doc, .docx, .xls, .xlsx, .csv, .tsv, .ppt, .pptx, .pages, .odt, .rtf, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.presentationml.presentation";

  if (!vaultId) console.warn('Missing vault id');

  const openDropzone = () => {
    setSelectedFile(undefined);
    if (dropzoneRef.current) {
      dropzoneRef.current.open();
    }
  };

  const fetchDocuments = () => {
    setIsLoading((prevState) => ({ ...prevState, documents: true }));
    uploadedDocuments.getUploadedDocuments(agencyClientId).then((resp) => {
      setIsLoading((prevState) => ({ ...prevState, documents: false }));
      setAccountConfirmed(!resp?.data?.account_confirmation_required);

      if (resp?.data?.display_doclocker_consent) {
        setShowConsent(true);
      } else if (resp?.data?.uploaded_documents) {
        setDocuments(resp.data.uploaded_documents);
      }
    });
  };

  const launchErrorModal = ({ title, message, buttonText }) => {
    enqueueModal({
      name: 'ErrorMessageModal',
      title: t(title),
      message: t(message),
      buttonText: t(buttonText),
      onButtonClick: () => {
        closeModal();
        openDropzone();
      },
      dialogClassName: unusedIsXSPhone ? '' : 'modal-40w'
    });
  };

  // create a preview as a side effect, whenever selected file is changed
  useEffect(() => {
    if (selectedFile && isEmpty(vaultId)) {
      addToast({
        message: t('Something went wrong. Try refreshing the page and trying again.'),
        type: 'error'
      });
      return;
    }
    if (!selectedFile) {
      setPreview(undefined);
      return;
    }

    const objectUrl = URL.createObjectURL(selectedFile);

    // Dividing the file size by a million bytes
    const MegaByte = 1048576;
    const size = (selectedFile.size / MegaByte).toFixed(2);

    const acceptedTypes = Object.keys(ACCEPTED_FILE_TYPES);
    const isValidFileType = acceptedTypes.some((acceptedType) => {
      if (acceptedType === 'image/*') {
        return selectedFile.type.startsWith('image/');
      }
      return selectedFile.type === acceptedType;
    });

    if (!isValidFileType) {
      launchErrorModal({ title: 'file not supported', message: 'file not supported message', buttonText: 'Upload document' });
      return;
    }

    if (size > 5) {
      launchErrorModal({ title: 'file too large', message: 'file too large message', buttonText: 'Upload document' });
      return;
    }

    setPreview(objectUrl);

    enqueueModal({
      name: 'UploadedDocumentModal',
      preview: objectUrl,
      file: selectedFile,
      vault_id: vaultId,
      agencyClientId: agencyClientId,
      track: track,
      openDropzone: () => openDropzone(),
      onUploadSuccess: fetchDocuments,
      dialogClassName: unusedIsXSPhone ? '' : 'modal-70w' // make the modal up to 70% wide
    });

    // free memory when ever this component is unmounted
    return () => URL.revokeObjectURL(objectUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFile]);

  useEffect(() => {
    fetchDocuments();
    if (agencyClientId) {
      AgencyConfiguration.get().then((resp) => {
        setIsPlusEnabled(resp?.data?.agency_configuration?.plus);
      });
    }
  }, []);

  useEffect(() => {
    if (account.data?.id) {
      setIsLoading(Object.assign(isLoading, { account: false }));
    }
  }, [account.data]);

  useEffect(() => {
    fetchDocuments();
  }, [currentLanguage, account.state.isLoggedIn, consentAgreed]);

  useEffect(() => {
    // Make sure to revoke the data uris to avoid memory leaks
    if (!selectedFile) {
      URL.revokeObjectURL(preview);
    }
  }, [selectedFile]);

  const onConsentSubmit = () => {
    setShowConsent(false);
    setConsentAgreed(true);
  };

  const UploadDocumentDropzone = ({ clearErrors }) => {
    return (
      <Dropzone
        ref={dropzoneRef}
        multiple={false}
        accept={ACCEPTED_FILE_TYPES}
        onDrop={(acceptedFiles) => {
          // if need to show multiple files, loop through here and use an array for selectedFiles
          if (clearErrors) {
            clearErrors();
          }
          if (acceptedFiles.length) {
            setSelectedFile(acceptedFiles[0]);
          } else {
            enqueueModal({
              name: 'ErrorMessageModal',
              title: t('file not supported'),
              message: t('file not supported message'),
              buttonText: t('Upload document'),
              onButtonClick: () => {
                closeModal();
                openDropzone();
              },
              dialogClassName: unusedIsXSPhone ? '' : 'modal-40w'
            });
          }
        }}
      >
        {({ getRootProps, getInputProps, acceptedFiles }) => {
          return (
            <div {...getRootProps({ className: 'dropzone' })}>
              <input data-cy="input-file-document-upload" {...getInputProps()} />
              {isLoading.documents ? (
                <Skeleton height={50} />
              ) : (
                <Button dataCy={'button-document-upload'} type="primary" text={t('Upload document')} styles={{ width: 'full' }} />
              )}
            </div>
          );
        }}
      </Dropzone>
    );
  };

  const openLogin = () => {
    enqueueModal({ name: 'LoginModal' });
  };

  const renderLoggedOut = () => {
    return (
      <div>
        <Row>
          <Col md={{ span: 8, offset: 2 }} xs="12">
            <Card data-cy="card-document-logged-out" className={classes.loggedOutCard}>
              <h5>{t('Please login to view your documents.')}</h5>
              {unusedIsPhone ||
                (unusedIsXSPhone && (
                  <>
                    <br />
                    <Button
                      data-cy="mobile-button-document-login"
                      type="primary"
                      shape="rounded"
                      onButtonClick={openLogin}
                      styles={{ width: 'full' }}
                      text={t('login')}
                    />
                  </>
                ))}
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  const renderLoadingDocuments = () => {
    return (
      <Row className="my-4">
        <Col xs={12} sm={{ span: 10, offset: 1 }} md={{ span: 8, offset: 2 }} lg={{ span: 6, offset: 3 }}>
          <div className={`${classes.stateContainer} ${classes.loadingContainer}`}>
            <h4>{t('Your documents are loading')}</h4>
            <h4>
              <FontAwesomeIcon icon={['fas', 'spinner']} className={`${classes.spinner} fa-spin`} />
            </h4>
          </div>
        </Col>
        <Skeleton height={500} />
      </Row>
    );
  };

  const renderDocuments = () => {
    return (
      <>
        <Row>
          {!agencyClientId && (
            <Col xs={12} md={{ span: 6 }} className={unusedIsPhone || unusedIsXSPhone ? 'mb-4' : ''}>
              <div className={`${classes.messageContainer}`}>
                <p
                  className="mb-0"
                  dangerouslySetInnerHTML={{
                    __html: t(
                      "Upload important documents to easily access later. They'll be safely stored and kept private and you can control who you share them with."
                    )
                  }}
                ></p>
              </div>
            </Col>
          )}
          {!unusedIsPhone && !unusedIsXSPhone && (
            <Col xs={12} md={{ span: 3, offset: agencyClientId ? 6 : 0 }}>
              <p className={`${classes.smallFont} text-end`}>{t('Upload a PDF, PNG, or JPG that is less than 5MB in size.')}</p>
            </Col>
          )}
          <Col xs={12} md={{ span: 3 }} className="text-end">
            <UploadDocumentDropzone />
          </Col>
          {(unusedIsPhone || unusedIsXSPhone) && (
            <Col xs={12} md={{ span: 3 }}>
              <p className="text-center">{t('Upload a PDF, PNG, or JPG that is less than 5MB in size.')}</p>
            </Col>
          )}
        </Row>
        <Row>
          <Col xs={12}>
            <div className={'my-4'}>
              <DocumentList
                documents={documents}
                agencyClientId={agencyClientId}
                setDocuments={setDocuments}
                fetchDocuments={fetchDocuments}
                isPlusEnabled={isPlusEnabled}
              />
            </div>
          </Col>
        </Row>
      </>
    );
  };

  const renderDocumentsEmpty = () => {
    return (
      <Col xs={12} sm={{ span: 10, offset: 1 }} md={{ span: 8, offset: 2 }} lg={{ span: 6, offset: 3 }}>
        <div className={`${classes.stateContainer}`}>
          {!agencyClient && (
            <p
              dangerouslySetInnerHTML={{
                __html: t(
                  "Upload important documents like your ID, utility bills, or Social Security card to easily access later. They'll be safely stored and kept private and you can control who you share them with."
                )
              }}
            ></p>
          )}
          {agencyClient && (
            <p>
              <em>
                {t("Client hasn't given you access to view any of their documents. But you can still upload documents to client's account.", {
                  client: agencyClient.first_name
                })}
              </em>
            </p>
          )}
          <p>
            <em>{t('Documents need to be a PDF, PNG, or JPG and less than 5MB in size.')}</em>
          </p>
          <UploadDocumentDropzone />
        </div>
      </Col>
    );
  };

  return (
    <Container>
      {!account.state.isLoggedIn && renderLoggedOut()}
      {account.state.isLoggedIn && !showConsent && (
        <>
          {isLoading.documents ||
            (isLoading.account && (
              <Row>
                <FontAwesomeIcon size="4x" icon={['fas', 'spinner']} className={`${classes.spinner} fa-spin`} />
              </Row>
            ))}
          {!isLoading.account && accountConfirmed && isLoading.documents && renderLoadingDocuments()}
          {!isLoading.documents && !isLoading.account && accountConfirmed && (
            <>
              {documents.length > 0 && renderDocuments()}
              {documents.length == 0 && renderDocumentsEmpty()}
            </>
          )}
          {account.state.isLoggedIn && !isLoading.account && accountConfirmed === false && <UserVerification></UserVerification>}
        </>
      )}
      {accountConfirmed && showConsent && agencyClientId && (
        <Row>
          <Col md={{ span: 8, offset: 2 }} xs="12" className={`${classes.stateContainer} ${classes.alignLeft} ${classes.consentContainer}`}>
            <DocumentConsent agencyClient={agencyClient} onConsentSubmit={onConsentSubmit}></DocumentConsent>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default Documents;
